import { useTranslation } from 'react-i18next';
import { ChevronDown, Check, CalendarClock } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/shared/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuLabel,
} from '@/shared/components/ui/dropdown-menu';
import type { TimelineZoom } from '../../types';

const ZOOMS: readonly TimelineZoom[] = ['7d', '14d', 'day', 'week', 'month'];

interface TimelineViewControlsProps {
  zoom: TimelineZoom;
  onZoomChange: (zoom: TimelineZoom) => void;
  onTodayClick: () => void;
}

/**
 * Timeline's view-specific toolbar controls — "Hoy" and the "Lapso" (zoom)
 * dropdown. Rendered into the shared `ActionToolbar`'s `viewControls` slot
 * rather than as a second toolbar row, so Timeline keeps the exact same
 * single-row header as List/Board/Calendar.
 *
 * There is no back/forward paging here on purpose: panning through time is the
 * grid's own horizontal scroll (same interaction model as the Gantt tab).
 */
export function TimelineViewControls({
  zoom,
  onZoomChange,
  onTodayClick,
}: TimelineViewControlsProps) {
  const { t } = useTranslation('tasks');

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={onTodayClick}
            className="gap-1.5"
            data-testid="tasks-timeline-today-button"
          >
            <CalendarClock className="size-4" />
            <span className="hidden md:inline">{t('timeline.toolbar.today')}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent className="md:hidden">{t('timeline.toolbar.today')}</TooltipContent>
      </Tooltip>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5"
            data-testid="tasks-timeline-zoom-menu"
          >
            <span>{t(`timeline.toolbar.zoom.options.${zoom}`)}</span>
            <ChevronDown className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t('timeline.toolbar.zoom.label')}</DropdownMenuLabel>
          {ZOOMS.map((option) => (
            <DropdownMenuItem
              key={option}
              onClick={() => onZoomChange(option)}
              data-testid={`tasks-timeline-zoom-${option}`}
              className="flex items-center justify-between gap-6"
            >
              {t(`timeline.toolbar.zoom.options.${option}`)}
              {zoom === option && <Check className="size-3.5" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
